import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

import { API_URL } from '../config/api';
const BACKEND_URL = API_URL;

// Configure axios defaults
axios.defaults.withCredentials = true;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [requiresVerification, setRequiresVerification] = useState(false);

  // Auto-attach helper function - processes pending invites
  const autoAttachPendingInvites = async () => {
    try {
      console.log('🔗 Auto-attaching user to pending group invites...');
      const response = await axios.post(`${BACKEND_URL}/api/groups/auto-attach`, {}, {
        withCredentials: true
      });
      console.log('✅ Auto-attach completed:', response.data);
      return response.data;
    } catch (error) {
      // Non-critical error - don't fail login if this fails
      console.warn('⚠️ Auto-attach failed (non-critical):', error.response?.data?.detail || error.message);
    }
  };

  // Check authentication status
  const checkAuthStatus = async () => {
    try {
      console.log('🔍 Checking authentication status...');
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`);
      
      if (response.data) {
        console.log('✅ User authenticated:', response.data.email);
        setUser(response.data);
        setIsAuthenticated(true);
        setRequiresVerification(!response.data.email_verified);
      }
    } catch (error) {
      console.log('❌ Auth check failed:', error.response?.status);
      
      // 429 = rate limited — do NOT clear auth state, just skip
      if (error.response?.status === 429) {
        console.log('⚠️ Rate limited on /me — keeping current auth state');
      } else if (isAuthenticated) {
        console.log('🚨 User was authenticated but /me failed - keeping session for now');
      } else {
        console.log('📤 No existing session, clearing auth state');
        setUser(null);
        setIsAuthenticated(false);
        setRequiresVerification(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Login function
  const login = async (credentials) => {
    try {
      console.log('🔑 Attempting login...');
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, credentials);
      
      if (response.data.status === 'MFA_REQUIRED') {
        console.log('🔐 MFA required');
        return { requiresMFA: true, data: response.data };
      }
      
      if (response.data.access_token) {
        console.log('✅ Login successful');
        setUser(response.data.user);
        setIsAuthenticated(true);
        setRequiresVerification(!response.data.user.email_verified);
        
        // Auto-attach to any pending group invites (non-blocking)
        await autoAttachPendingInvites();
        
        // Check if there's a redirect URL (e.g., from invite link)
        const redirectUrl = sessionStorage.getItem('redirect_after_login');
        if (redirectUrl) {
          sessionStorage.removeItem('redirect_after_login');
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 500);
        }
        
        return { success: true, data: response.data };
      }
    } catch (error) {
      console.error('❌ Login failed:', error.response?.data?.detail);
      throw error;
    }
  };

  // Signup function
  const signup = async (userData) => {
    try {
      console.log('📝 Attempting signup...');
      const response = await axios.post(`${BACKEND_URL}/api/auth/signup`, userData);
      
      if (response.data.access_token) {
        console.log('✅ Signup successful');
        setUser(response.data.user);
        setIsAuthenticated(true);
        setRequiresVerification(response.data.requires_verification || !response.data.user.email_verified);
        
        // Auto-attach to any pending group invites (non-blocking)
        await autoAttachPendingInvites();

        // Honor redirect_after_login (e.g., invite link)
        const redirectUrl = sessionStorage.getItem('redirect_after_login');
        if (redirectUrl) {
          sessionStorage.removeItem('redirect_after_login');
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 500);
        }

        return { success: true, data: response.data };
      }
    } catch (error) {
      console.error('❌ Signup failed:', error.response?.data?.detail);
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      console.log('👋 Logging out - calling backend...');
      
      // Call backend logout endpoint with credentials
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/logout`,
        {},
        { withCredentials: true }
      );
      
      console.log('✅ Backend logout successful:', response.data);
    } catch (error) {
      console.error('❌ Logout API call failed:', error);
      // Continue with local logout even if backend fails
    } finally {
      // Always clear local state
      console.log('🧹 Clearing local state and cookies...');
      setUser(null);
      setIsAuthenticated(false);
      setRequiresVerification(false);
      
      // Clear all auth-related cookies
      document.cookie = 'payo_session=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; samesite=none';
      document.cookie = 'access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;';
      
      // Redirect to login page
      console.log('↩️ Redirecting to login...');
      window.location.href = '/login?loggedOut=1';
    }
  };

  // Resend verification
  const resendVerification = async () => {
    try {
      console.log('📧 Resending verification email...');
      const response = await axios.post(`${BACKEND_URL}/api/auth/resend-verification`);
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error('❌ Resend verification failed:', error.response?.data?.detail);
      throw error;
    }
  };

  // Verify email
  const verifyEmail = async (token) => {
    try {
      console.log('✅ Verifying email...');
      const response = await axios.get(`${BACKEND_URL}/api/auth/verify?token=${token}`);
      
      if (response.data.message) {
        // Refresh auth status after verification
        await checkAuthStatus();
        return { success: true, message: response.data.message };
      }
    } catch (error) {
      console.error('❌ Email verification failed:', error.response?.data?.detail);
      throw error;
    }
  };

  // Forgot password
  const forgotPassword = async (email) => {
    try {
      console.log('🔄 Sending password reset email...');
      const response = await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error('❌ Forgot password failed:', error.response?.data?.detail);
      throw error;
    }
  };

  // Reset password
  const resetPassword = async (token, newPassword) => {
    try {
      console.log('🔄 Resetting password...');
      const response = await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error('❌ Reset password failed:', error.response?.data?.detail);
      throw error;
    }
  };

  // Verify 2FA login
  const verify2FALogin = async (email, password, code) => {
    try {
      console.log('🔐 Verifying 2FA login...');
      const response = await axios.post(`${BACKEND_URL}/api/auth/verify-2fa-login`, {
        email,
        password,
        code
      });
      
      if (response.data.access_token) {
        console.log('✅ 2FA verification successful');
        setUser(response.data.user);
        setIsAuthenticated(true);
        setRequiresVerification(!response.data.user.email_verified);
        
        // Auto-attach to any pending group invites (non-blocking)
        await autoAttachPendingInvites();

        // Honor redirect_after_login (e.g., invite link)
        const redirectUrl = sessionStorage.getItem('redirect_after_login');
        if (redirectUrl) {
          sessionStorage.removeItem('redirect_after_login');
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 500);
        }

        console.log('🎉 Authentication state updated - user should be logged in');
        return { success: true, data: response.data };
      }
    } catch (error) {
      console.error('❌ 2FA verification failed:', error.response?.data?.detail);
      throw error;
    }
  };

  // Initialize auth check
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    requiresVerification,
    login,
    signup,
    logout,
    checkAuthStatus,
    resendVerification,
    verifyEmail,
    forgotPassword,
    resetPassword,
    verify2FALogin
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;